class FinanceModule extends HTMLElement {
  connectedCallback () {
    this.financeModuleContent = this.querySelector('.finance-module__content')
    this.financeLinkText = this.querySelector('.price__finance-link-text')

    if (this.financeModuleContent && this.financeLinkText) {
      this.loading = true
      this.updateFinanceFromText(this.financeLinkText)

      const displayType = this.dataset.financeDisplayType || 'modal'

      if (displayType === 'link') {
        // Link mode: Navigate to external URL
        this.linkClickHandler = (event) => {
          event.preventDefault()
          const linkUrl = this.dataset.financeLinkUrl
          if (linkUrl) {
            window.open(linkUrl, '_blank')
          } else {
            console.warn('Finance link URL not configured')
          }
        }
        this.addEventListener('click', this.linkClickHandler)
      } else {
        // Modal mode: Open finance calculator modal
        this.modalClickHandler = (event) => {
          event.preventDefault()
          this.openModal()
        }
        this.addEventListener('click', this.modalClickHandler)
      }
    } else {
      console.error('Finance module content not found.')
    }

    // Listen for variant changes to sync loading state
    this.onVariantChange = this.onVariantChange.bind(this)
    document.addEventListener('variantChange', this.onVariantChange)
  }

  disconnectedCallback () {
    document.removeEventListener('variantChange', this.onVariantChange)
  }

  onVariantChange () {
    // Immediately go into loading state when variant changes
    this.loading = true
  }

  updateFinanceFromText (financeLinkText) {
    const priceInCents = parseFloat(this.price)
    const price = priceInCents / 100

    // Parse finance terms to find the longest term period
    const financeTerms = window.Tentbox.financeTerms || this.parseFinanceTerms(this.dataset.financeTerms)

    if (!financeTerms.length) {
      console.warn('No valid finance terms found; hiding finance link.')
      this.loading = false
      return
    }

    // Find the term with the longest period
    const longestTerm = financeTerms.reduce((prev, current) =>
      (prev.period > current.period) ? prev : current,
    )

    const PT = longestTerm.period
    const AIR = longestTerm.apr
    let monthlyPayment

    if (AIR === 0) {
      // 0% APR
      monthlyPayment = price / PT
    } else {
      // Calculate monthly payment using compound interest
      const PIR = Math.pow(1 + AIR / 100, 1 / 12) - 1
      const onePlusPIRToPT = Math.pow(1 + PIR, PT)
      const numerator = price * PIR * onePlusPIRToPT
      const denominator = onePlusPIRToPT - 1
      monthlyPayment = numerator / denominator
    }

    // Round UP to nearest currency unit
    monthlyPayment = Math.ceil(monthlyPayment)

    // Format the monthly payment
    const formattedMonthlyPayment = this.formatPrice(monthlyPayment)

    // Get the translation string and replace the placeholder
    let financeFromTranslation = this.dataset.financeFromTranslation
    financeFromTranslation = financeFromTranslation.replace('{{ price }}', formattedMonthlyPayment)
    if (financeLinkText) {
      financeLinkText.textContent = financeFromTranslation
    }

    // Update the finance link text with a small delay for smooth transition
    setTimeout(() => {
      this.loading = false
    }, 100)
  }

  openModal () {
    // Open the modal first
    window.dispatchEvent(
      new CustomEvent('open:modal', {
        detail: {
          width: 'min(600px, 100svw)',
        },
      }),
    )

    // Wait for the modal to be available in the DOM
    const checkModalReady = () => {
      this.modalHeaderTitle = this.modal.querySelector('.reusable-modal__title')
      this.modalBody = this.modal.querySelector('.reusable-modal__body')

      if (!this.modalHeaderTitle || !this.modalBody) {
        requestAnimationFrame(checkModalReady)
        return
      }

      // Modal is ready
      this.setModalContent()
    }

    checkModalReady()
  }

  setModalContent () {
    const priceInCents = parseFloat(this.price)
    if (isNaN(priceInCents)) {
      console.error('Invalid price data.')
      return
    }
    const price = priceInCents / 100

    const layout = this.dataset.financeModalLayout || 'table'

    const financeTerms = window.Tentbox.financeTerms || this.parseFinanceTerms(this.dataset.financeTerms)
    if (layout === 'table' && !financeTerms.length) {
      console.error('No valid finance terms found.')
      return
    }

    // Simple HTML decode helper
    const decodeHtml = (html) => {
      const txt = document.createElement('textarea')
      txt.innerHTML = html
      return txt.value
    }

    let modalContent = ''
    if (layout === 'richtext') {
      modalContent = `
        <div class="finance-module flex flex-col gap-4">
          ${this.dataset.financeRichBody ? `<div class="tb-in-page-content tb-list-fix">${decodeHtml(this.dataset.financeRichBody)}</div>` : ''}
        </div>
      `
    } else {
      modalContent = `
        <div class="finance-module flex flex-col gap-4">
          <div class="finance-module__section flex flex-col">
            <div class="finance-module__section-title font-body text-lg font-semibold pb-2">${this.financeTermsTitle}</div>
            ${
              this.financeTermsMessage
                ? `
            <div class="finance-module__section-message tb-in-page-content tb-list-fix">
              ${decodeHtml(this.financeTermsMessage)}
            </div>
            `
                : ''
            }
            <div class="overflow-hidden rounded-lg border border-steel">
              <table id="finance-options-table" class="w-full border-collapse text-sm">
                <thead>
                  <tr class="bg-oil text-white">
                    <th class="p-2 text-left">${this.termTranslation}</th>
                    <th class="p-2 text-left">${this.aprTranslation}</th>
                    <th class="p-2 text-left">${this.monthlyPaymentTranslation}</th>
                    <th class="p-2 text-right">${this.totalPayableTranslation}</th>
                  </tr>
                </thead>
                <tbody>
                  ${this.generateFinanceOptions(price, financeTerms)}
                </tbody>
              </table>
            </div>
            ${
              this.postTableMessage
                ? `
            <div class="finance-module__section-message tb-in-page-content tb-list-fix mt-4">
              ${decodeHtml(this.postTableMessage)}
            </div>
            `
                : ''
            }
            ${
              this.legalMessage
                ? `
            <div class="finance-module__legal text-sm text-tugboat text-left ${
                  this.postTableMessage ? 'mt-4' : 'mt-8'
                } tb-in-page-content tb-list-fix">
              ${decodeHtml(this.legalMessage)}
            </div>
            `
                : ''
            }
          </div>
        </div>
      `
    }

    this.modalHeaderTitle.innerHTML = `<div class="flex items-center gap-2">${this.modalTitle} ${
      this.modalLogo ? `<img src="${this.modalLogo}" alt="${this.modalTitle}" />` : ''
    }</div>`
    this.modalBody.innerHTML = modalContent
  }

  generateFinanceOptions (price, financeTerms) {
    const LV = price

    let financeOptionsHtml = ''

    financeTerms.forEach((term, index) => {
      const PT = term.period
      const AIR = term.apr
      let P, totalInterest, totalPayable

      if (AIR === 0) {
        P = LV / PT
        totalInterest = 0
        totalPayable = LV
      } else {
        const PIR = Math.pow(1 + AIR / 100, 1 / 12) - 1
        const onePlusPIRToPT = Math.pow(1 + PIR, PT)
        const numerator = LV * PIR * onePlusPIRToPT
        const denominator = onePlusPIRToPT - 1
        P = numerator / denominator

        totalInterest = P * PT - LV
        totalPayable = LV + totalInterest
      }

      P = Math.ceil(P)
      totalInterest = Math.ceil(totalInterest)
      totalPayable = Math.ceil(totalPayable)

      const aprDisplay = `${AIR}%`
      const rowClass = index % 2 === 0 ? 'bg-white' : 'bg-sheep'
      const monthlyPaymentClass = index % 2 === 0 ? 'bg-orange-200' : 'bg-orange-300'

      financeOptionsHtml += `
        <tr class="${rowClass}">
          <td class="p-2 text-left">${term.title}</td>
          <td class="p-2 text-left">${aprDisplay}</td>
          <td class="p-2 text-left font-semibold ${monthlyPaymentClass}">${this.formatPrice(P)} / mo </td>
          <td class="p-2 text-right">${this.formatPrice(totalPayable)}</td>
        </tr>
      `
    })

    return financeOptionsHtml
  }

  formatPrice (amount) {
    const currencySymbol = this.getCurrencySymbol()
    const marketCode = (window.Tentbox?.market?.code || window.location.pathname.split('/')[1]?.split('-')[0] || '').toLowerCase()
    const rounded = Math.round(amount)
    if (currencySymbol === '€') {
      return marketCode === 'de' ? `${rounded} ${currencySymbol}` : `${rounded}${currencySymbol}`
    }
    return `${currencySymbol}${rounded}`
  }

  getCurrencySymbol () {
    const currencyCode = window.Shopify.currency.active
    switch (currencyCode) {
      case 'USD':
        return '$'
      case 'EUR':
        return '€'
      case 'GBP':
        return '£'
      default:
        return ''
    }
  }

  get modal () {
    const modalElement = document.querySelector('.reusable-modal')
    if (!modalElement) {
      console.error('Modal element not found.')
    }
    return modalElement
  }

  get modalTitle () {
    const title = this.dataset.financeModalTitle
    if (!title) {
      console.warn('Modal title not found.')
    }
    return title
  }

  get modalLogo () {
    return this.dataset.financeModalLogo || ''
  }

  get financeTermsMessage () {
    return this.dataset.financeTermsMessage || ''
  }

  get postTableMessage () {
    return this.dataset.financePostTableMessage || ''
  }

  get legalMessage () {
    return this.dataset.financeLegalMessage || ''
  }

  get termTranslation () {
    return this.dataset.financeTermTranslation || 'Term'
  }

  get aprTranslation () {
    return this.dataset.financeAprTranslation || 'Interest Rate'
  }

  get monthlyPaymentTranslation () {
    return this.dataset.financeMonthlyPaymentTranslation || 'Monthly Payment'
  }

  get totalPayableTranslation () {
    return this.dataset.financeTotalPayableTranslation || 'Total Payable'
  }

  get financeTermsTitle () {
    return this.dataset.financeTermsTitle || 'Finance Options:'
  }

  get price () {
    const price = this.dataset.productPrice
    if (!price) {
      console.error('Product price not found.')
    }
    return price
  }

  parseFinanceTerms (data) {
    const terms = []
    if (!data) {
      console.warn('No finance terms data provided.')
      return terms
    }

    const termStrings = data.split(',')
    termStrings.forEach((termStr) => {
      const [title, apr, period] = termStr.split('|')
      if (title && apr && period) {
        terms.push({
          title: title.trim(),
          apr: parseFloat(apr),
          period: parseInt(period, 10),
        })
      } else {
        console.warn(`Invalid finance term: ${termStr}`)
      }
    })
    return terms
  }

  set loading (val) {
    const productPrice = this.closest('.product-price')
    if (productPrice) {
      if (val) {
        productPrice.setAttribute('loading', 'true')
        productPrice.setAttribute('aria-busy', 'true')
      } else {
        productPrice.removeAttribute('loading')
        productPrice.removeAttribute('aria-busy')
      }
    }
  }

  get loading () {
    const productPrice = this.closest('.product-price')
    if (productPrice) {
      return productPrice.getAttribute('loading') === 'true'
    }
    return false
  }
}

if (!customElements.get('finance-module')) {
  customElements.define('finance-module', FinanceModule)
}
